import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import dartsHubApi from '../axios/dartsHubApi';
import { backend_url, dartshub_url, auth_url, app_url, getCurrentSeason } from '@/config/config';
import { useRouter } from 'vue-router';
import { normalize } from '../functions/utils';
import Cookies from 'js-cookie';
import axios from 'axios';
import { HOST_LOGIN_REQUEST_MESSAGE, isHostEmbed } from '@/embedHost';

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter();
  // Rôles mémorisés : ne comptent qu'avec un jeton valide (cf. `scopes` plus bas), sinon un
  // jeton expiré laissait l'interface admin visible à un visiteur affiché comme déconnecté.
  const storedScopes = useStorage<string[]>('scopes', [], localStorage, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : []),
      write: (v) => JSON.stringify(v),
    },
  });

  const showAuthModal = ref(false);
  const token = useStorage<string>('token', '');
  const refreshToken = useStorage<string>('refreshToken', '');
  const email = useStorage<string>('email', '');
  const name = useStorage<string>('name', '');
  const nickname = useStorage<string>('nickname', '');
  const firstname = useStorage<string>('firstname', '');
  const profile_picture = useStorage<string>('profile_picture', '', localStorage);
  const bio = useStorage<string>('bio', '', localStorage);
  const userId = useStorage<number | null>('userId', null, localStorage, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  });
  const license = useStorage<License | null>('license', null, localStorage, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  });
  const associated_club_number = useStorage<number | null>('associated_club_number', null, localStorage, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  });
  const associated_club_name = useStorage<string>('associated_club_name', '', localStorage);

  // Make profilePictureUrl a computed property
  const profilePictureUrl = computed(() => {
    return profile_picture.value ? `${dartshub_url}${profile_picture.value}` : '';
  });

  // Gestion du cookie cookie_consent
  const cookieConsent = ref<string | null>(Cookies.get('cookie_consent') || null);

  // Synchroniser cookieConsent avec le cookie
  watch(cookieConsent, (newValue) => {
    if (newValue === null) {
      Cookies.remove('cookie_consent');
    } else {
      Cookies.set('cookie_consent', newValue, { secure: true, sameSite: 'strict', expires: 365 });
    }
  });

  const cookiesAccepted = computed(() => cookieConsent.value === 'accepted');
  const isConsentGiven = computed(() => cookieConsent.value !== null);

  const isFetchingLicense = ref(false);

  // Intégré par un site hôte (cf. embedHost.ts) : la connexion se fait sur le site hôte,
  // qui renvoie ensuite sa session à l'iframe.
  const openAuthModal = () => {
    if (isHostEmbed) {
      window.parent.postMessage({ type: HOST_LOGIN_REQUEST_MESSAGE }, '*');
      return;
    }
    showAuthModal.value = true;
  };

  const closeAuthModal = () => {
    showAuthModal.value = false;
  };

  const setAuthData = (auth: {
    token: string;
    refreshToken?: string;
    email: string;
    name: string;
    nickname: string;
    firstname: string;
    profile_picture: string;
    bio: string;
    userId?: number;
    scopes: string[];
    license?: License | null;
    associated_club_name?: string | null;
    associated_club_number?: number | null;
  }) => {
    token.value = auth.token;
    if (auth.refreshToken) refreshToken.value = auth.refreshToken;

    email.value = auth.email;
    name.value = auth.name;
    nickname.value = auth.nickname;
    firstname.value = auth.firstname;
    profile_picture.value = auth.profile_picture || '';
    bio.value = auth.bio || '';
    userId.value = auth.userId || null;
    storedScopes.value = auth.scopes || [];

    if (auth.license !== undefined) license.value = auth.license;

    associated_club_name.value = auth.associated_club_name ?? '';
    associated_club_number.value = auth.associated_club_number ?? null;
  };

  const logout = () => {
    localStorage.clear();
    token.value = '';
    refreshToken.value = '';
    email.value = '';
    name.value = '';
    nickname.value = '';
    firstname.value = '';
    profile_picture.value = '';
    bio.value = '';
    userId.value = null;
    storedScopes.value = [];
    license.value = null;
    associated_club_name.value = '';
    associated_club_number.value = null;
    // Intégré par un site hôte : on reste sur la page affichée dans l'iframe.
    if (!isHostEmbed) router.push('/');
  };

  const acceptCookies = () => {
    cookieConsent.value = 'accepted';
  };

  const rejectCookies = () => {
    cookieConsent.value = 'rejected';
    logout();
  };

  const resetConsent = () => {
    cookieConsent.value = null;
  };

  const isAuthenticated = computed(() => {
    return !!token.value && isTokenValid(token.value);
  });

  const scopes = computed(() => (isAuthenticated.value ? storedScopes.value : []));

  const purgeIfInvalid = () => {
    if (!isAuthenticated.value && (token.value || name.value || storedScopes.value.length)) {
      logout();
    }
  };

  const proxyAuthToken = ref<string | null>(null);

  const setAuthToken = (token: string | null) => {
    proxyAuthToken.value = token;
  };

  const isTokenValid = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiration = payload.exp * 1000;
      return Date.now() < expiration;
    } catch (error) {
      console.error('Erreur lors de la validation du token:', error);
      return false;
    }
  };

  const authRedirectCallback = () => {
    const current = window.location.pathname + window.location.search;
    const callbackBase = `${app_url}/auth/callback`;
    return current !== '/' && !current.startsWith('/auth/')
      ? `${callbackBase}?return_to=${encodeURIComponent(current)}`
      : callbackBase;
  };

  const redirectToLogin = () => {
    window.location.href = `${auth_url}/login?redirect=${encodeURIComponent(authRedirectCallback())}`;
  };

  const redirectToRegister = () => {
    window.location.href = `${auth_url}/register?redirect=${encodeURIComponent(authRedirectCallback())}`;
  };

  const setFromCallback = (params: URLSearchParams) => {
    const t = params.get('token');
    const r = params.get('refresh');
    const e = params.get('email');
    const n = params.get('name') || '';
    const fn = params.get('firstname') || '';
    const nn = params.get('nickname') || '';
    const pp = params.get('profile_picture') || '';
    const b = params.get('bio') || '';
    const uid = params.get('userId');
    const sc = params.get('scopes');

    if (!t || !e) return false;

    setAuthData({
      token: t,
      refreshToken: r || '',
      email: e,
      name: n,
      firstname: fn,
      nickname: nn,
      profile_picture: pp,
      bio: b,
      userId: uid ? Number(uid) : undefined,
      scopes: sc ? sc.split(',').filter(Boolean) : [],
    });
    return true;
  };

  const refreshAccessToken = async () => {
    if (!refreshToken.value) {
      console.error('No refresh token available');
      logout(); // Log out if no refresh token
      return false;
    }

    try {
      const response = await axios.post(
        `${dartshub_url}/auth/refresh`,
        {},  // body vide
        {
          headers: {
            Authorization: `Bearer ${refreshToken.value}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.access_token) {
        setAuthData({
          token: response.data.access_token,
          refreshToken: response.data.refresh_token,
          email: email.value,
          name: name.value,
          firstname: firstname.value,
          nickname: nickname.value,
          profile_picture: profile_picture.value,
          bio: bio.value,
          scopes: storedScopes.value,
          license: license.value,
          associated_club_number: associated_club_number.value,
          associated_club_name: associated_club_name.value,
        });
        return true; // Refresh successful
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('Refresh token failed:', error);
      logout(); // Log out on refresh failure
      return false;
    }
  };

  const currentUserName = computed(() => (name.value + ' ' + firstname.value || '').trim());

  const isCurrentUser_inscription = (inscription: InscriptionResponse) => {
    const normalizedPlayer = normalize(`${inscription.name} ${inscription.firstname}`);
    const normalizedUser = normalize(currentUserName.value);

    // Split into words and convert to sets to ignore order
    const playerWords = new Set(normalizedPlayer.split(/\s+/).filter(word => word));
    const userWords = new Set(normalizedUser.split(/\s+/).filter(word => word));

    // Check if the sets are identical
    if (playerWords.size !== userWords.size) return false;
    return [...userWords].every(word => playerWords.has(word));
  };

  const isCurrentUser_leaderboard = (joueur: string) => {
    // Remove all parenthetical content
    const cleanedPlayer = joueur.replace(/\s*\([^)]*\)/g, '').trim();

    // Split into individual players if it's a pair
    const players = cleanedPlayer.split(/\s*[-\/]\s*/).map(p => p.trim()).filter(p => p);

    const normalizedUser = normalize(currentUserName.value);
    const userWords = new Set(normalizedUser.split(/\s+/).filter(word => word));

    // Check each player
    for (const player of players) {
      const normalizedPlayer = normalize(player);
      const playerWords = new Set(normalizedPlayer.split(/\s+/).filter(word => word));

      if (playerWords.size === userWords.size && [...userWords].every(word => playerWords.has(word))) {
        return true;
      }
    }

    return false;
  };

  const fetchLicense = async () => {
    if (isFetchingLicense.value || !isAuthenticated.value) {
      return;
    }
    isFetchingLicense.value = true;
    try {
      const response = await dartsHubApi.get<License[]>('/licenses/me');
      const season = getCurrentSeason();
      license.value = response.data.find(l => l.season === season) ?? response.data[0] ?? null;
    } catch (error: any) {
      console.error('Fetch license error:', error);
      license.value = null; // Met la licence à null en cas d'erreur
    } finally {
      isFetchingLicense.value = false;
    }
  };

  const fetchCurrentUser = async () => {
    if (!token.value || !isAuthenticated.value) return;

    try {
      const response = await axios.get(`${dartshub_url}/users/me`, {
        headers: { Authorization: `Bearer ${token.value}` },
      });

      const data = response.data;

      setAuthData({
        token: token.value,
        email: data.email,
        name: data.name,
        nickname: data.nickname || '',
        firstname: data.firstname,
        profile_picture: data.profile_picture || '',
        bio: data.bio || '',
        userId: data.id,
        scopes: data.scopes || [],
        license: license.value,
        associated_club_name: data.club_name ?? null,
        associated_club_number: data.club_number ?? null,
      });

      console.info(`✅ Club chargé pour ${data.nickname || data.firstname} → ${data.club_name} (${data.club_number})`);
    } catch (error) {
      console.error('❌ Erreur fetchCurrentUser (/users/me):', error);
    }
  };

  return {
    token,
    refreshToken,
    email,
    name,
    nickname,
    firstname,
    profile_picture,
    profilePictureUrl,
    bio,
    userId,
    scopes,
    cookieConsent,
    cookiesAccepted,
    isConsentGiven,
    setAuthData,
    logout,
    acceptCookies,
    rejectCookies,
    resetConsent,
    isAuthenticated,
    purgeIfInvalid,
    proxyAuthToken,
    setAuthToken,
    isTokenValid,
    refreshAccessToken,
    license,
    fetchLicense,
    showAuthModal,
    openAuthModal,
    closeAuthModal,
    isCurrentUser_inscription,
    isCurrentUser_leaderboard,
    fetchCurrentUser,
    associated_club_number,
    associated_club_name,
    redirectToLogin,
    redirectToRegister,
    setFromCallback,
  };
});